import React from 'react';
import { HostBalance } from '../../services/billingService';
import { useAppSelector } from '../../store/hooks';
import { Wallet, TrendingUp, TrendingDown, Minus } from 'lucide-react';
import clsx from 'clsx';

interface BalanceDisplayProps {
  balance: HostBalance;
  showDetails?: boolean;
  className?: string;
}

const BalanceDisplay: React.FC<BalanceDisplayProps> = ({
  balance,
  showDetails = true,
  className
}) => {
  const { theme } = useAppSelector((state) => state.ui);
  const balanceAmount = parseFloat(balance.balance);
  const isNegative = balanceAmount < 0;
  const isZero = balanceAmount === 0;
  const isPositive = balanceAmount > 0;

  return (
    <div className={clsx(
      'rounded-lg border-2 p-6 transition-all',
      theme === 'dark'
        ? 'bg-gray-900/80 border-gray-700'
        : 'bg-white border-gray-200',
      className
    )}>
      <div className="flex items-start justify-between">
        <div className="flex-1">
          {showDetails && (
            <div className="mb-4">
              <h3 className={`text-sm font-medium mb-1 ${
                theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
              }`}>
                Host
              </h3>
              <p className={`text-base font-semibold ${
                theme === 'dark' ? 'text-white' : 'text-gray-900'
              }`}>
                {balance.host_name}
              </p>
              {balance.host_phone && (
                <p className={`text-sm mt-1 ${
                  theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                }`}>
                  {balance.host_phone}
                </p>
              )}
            </div>
          )}

          <div className="flex items-center space-x-3">
            <div className={clsx(
              'p-3 rounded-lg',
              isNegative
                ? 'bg-red-100 dark:bg-red-900/20'
                : isZero
                ? 'bg-gray-100 dark:bg-gray-800'
                : 'bg-green-100 dark:bg-green-900/20'
            )}>
              <Wallet className={clsx(
                'h-6 w-6',
                isNegative
                  ? 'text-red-600 dark:text-red-400'
                  : isZero
                  ? 'text-gray-600 dark:text-gray-400'
                  : 'text-green-600 dark:text-green-400'
              )} />
            </div>
            <div>
              <p className={`text-sm font-medium ${
                theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
              }`}>
                Current Balance
              </p>
              <p className={clsx(
                'text-3xl font-bold mt-1',
                isNegative
                  ? 'text-red-600 dark:text-red-400'
                  : isZero
                  ? theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                  : 'text-green-600 dark:text-green-400'
              )}>
                {balanceAmount.toFixed(2)} JOD
              </p>
            </div>
          </div>

          <div className="mt-4 flex items-center space-x-4">
            {isNegative && (
              <div className="flex items-center space-x-1 text-red-600 dark:text-red-400">
                <TrendingDown className="h-4 w-4" />
                <span className="text-sm font-medium">Negative Balance</span>
              </div>
            )}
            {isZero && (
              <div className="flex items-center space-x-1 text-gray-600 dark:text-gray-400">
                <Minus className="h-4 w-4" />
                <span className="text-sm font-medium">Zero Balance</span>
              </div>
            )}
            {isPositive && (
              <div className="flex items-center space-x-1 text-green-600 dark:text-green-400">
                <TrendingUp className="h-4 w-4" />
                <span className="text-sm font-medium">Positive Balance</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BalanceDisplay;

